var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { version, walkDelta, computeForkMeet, } from "@hyper-hyper-space/hhs3_mvt";
export class RCapDelta {
    constructor(start, end, revisionBound, root) {
        this.start = start;
        this.end = end;
        this.revisionBound = revisionBound;
        this.type = root.type;
        this.changes = root.changes;
        this.nested = root.nested;
    }
    getRevisionBound() { return this.revisionBound; }
    get identityChanges() { return this.changes.identityChanges; }
    get capabilityChanges() { return this.changes.capabilityChanges; }
    get grantChanges() { return this.changes.grantChanges; }
}
function emptyRCapChanges(type) {
    return {
        type,
        changes: { identityChanges: [], capabilityChanges: [], grantChanges: [] },
        nested: new Map(),
    };
}
function addGrantPair(grantPairs, capName, keyId) {
    if (!grantPairs.has(capName))
        grantPairs.set(capName, new Set());
    grantPairs.get(capName).add(keyId);
}
// Accumulator for RCap. Collects the candidate subjects (identities, capability names,
// grant pairs) touched by each ingested entry, then compares the start and end views once
// per subject in finalize. RCap has no nested objects, so the nested map is always empty.
export class RCapDeltaAccumulator {
    constructor(cap, start, end) {
        this.cap = cap;
        this.start = start;
        this.end = end;
        this.keyIds = new Set();
        this.grantPairs = new Map();
        this.capNames = new Set(Object.keys(cap.getInitialCaps()));
    }
    ingest(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            const p = entry.payload;
            switch (p.action) {
                case 'add-identity':
                    this.keyIds.add(p.keyId);
                    return true;
                case 'create-cap':
                    this.capNames.add(p.capName);
                    return true;
                case 'delete-cap':
                    this.capNames.add(p.capName);
                    return true;
                case 'grant':
                case 'revoke':
                    addGrantPair(this.grantPairs, p.capName, p.grantee);
                    return true;
            }
            return false;
        });
    }
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            const cap = this.cap;
            const startView = yield cap.getView(this.start, this.start);
            const endView = yield cap.getView(this.end, this.end);
            const identityChanges = [];
            for (const keyId of this.keyIds) {
                const wasIdentity = yield startView.isIdentity(keyId);
                const nowIdentity = yield endView.isIdentity(keyId);
                if (wasIdentity !== nowIdentity) {
                    identityChanges.push({ keyId, added: nowIdentity });
                }
            }
            const capabilityChanges = [];
            for (const capName of this.capNames) {
                const existed = yield startView.capabilityExists(capName);
                const exists = yield endView.capabilityExists(capName);
                if (existed !== exists) {
                    capabilityChanges.push({ capName, existed, exists });
                }
            }
            const grantChanges = [];
            const endCapExists = new Map();
            for (const [capName, grantKeyIds] of this.grantPairs) {
                let capExistsInEnd = endCapExists.get(capName);
                if (capExistsInEnd === undefined) {
                    capExistsInEnd = yield endView.capabilityExists(capName);
                    endCapExists.set(capName, capExistsInEnd);
                }
                if (!capExistsInEnd)
                    continue;
                for (const keyId of grantKeyIds) {
                    if (cap.isCreator(keyId))
                        continue;
                    const wasGranted = yield startView.hasCapability(keyId, capName);
                    const nowGranted = yield endView.hasCapability(keyId, capName);
                    if (wasGranted !== nowGranted) {
                        grantChanges.push({ keyId, capName, wasGranted, nowGranted });
                    }
                }
            }
            return {
                type: cap.getType(),
                changes: { identityChanges, capabilityChanges, grantChanges },
                nested: new Map(),
            };
        });
    }
}
export function computeRCapDelta(cap, rawDag, strategy, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        if (strategy === 'bounded')
            return computeDeltaBounded(cap, rawDag, start, end);
        if (strategy === 'full')
            return computeDeltaFull(cap, rawDag, start, end);
        throw new Error("Invalid delta strategy: " + strategy);
    });
}
function computeDeltaFull(cap, rawDag, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        // Full scan: empty bound, so the walk visits the entire history of `end`.
        const root = yield walkDelta(rawDag, start, end, version(), cap.createDeltaAccumulator(start, end));
        return new RCapDelta(start, end, version(), root);
    });
}
function computeDeltaBounded(cap, rawDag, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        const fork = yield rawDag.findForkPosition(start, end);
        if (fork.forkA.size > 0) {
            throw new Error("bounded computeDelta requires END to extend START");
        }
        if (fork.forkB.size === 0) {
            return new RCapDelta(start, end, fork.commonFrontier, emptyRCapChanges(cap.getType()));
        }
        const revisionBound = yield computeForkMeet(rawDag, fork.common);
        const root = yield walkDelta(rawDag, start, end, revisionBound, cap.createDeltaAccumulator(start, end));
        return new RCapDelta(start, end, revisionBound, root);
    });
}
